<!-- Responsibility: Implement combat modal behavior for the ui/components surface.
Scope: Owns ui/components concerns only; cross-surface orchestration is excluded. -->

<combat-modal>
  <dialog id="combat-modal" class="combat-modal" onclick={ onBackdrop }>
    <div class="cmodal-content" onclick={ (e) => e.stopPropagation() }>

      <div class="cmodal-header">
        <div class="cmodal-title-group">
          <span class="cmodal-icon">⚔</span>
          <span class="cmodal-title">COMBAT</span>
          <span class="cmodal-sep">—</span>
          <span class="cmodal-round">ROUND { state.round }</span>
        </div>
        <button
          type="button"
          class="cmodal-close modal-close-hit tooltip-wrap"
          aria-label="Close combat"
          aria-disabled={ !state.outcome ? 'true' : 'false' }
          data-action="close"
        >
          ✕
          <div class="tooltip" data-position="bottom" data-tip-custom="true">
            { state.outcome ? 'Return to scene' : 'Locked until combat outcome' }
          </div>
        </button>
      </div>

      <!-- Combatants -->
      <div class="cmodal-combatants">
        <!-- Enemy -->
        <div class="cmodal-actor enemy">
          <div class="cmodal-actor-name">{ state.npcName.toUpperCase() }</div>
          <div class="cmodal-vitals-container">
            <div class="cmodal-bar enemy">
              <div class="cmodal-fill { state.npcVitality > 50 ? 'high' : state.npcVitality > 25 ? 'mid' : 'low' }" style="width:{ state.npcVitality }%"></div>
            </div>
            <span class="cmodal-pct">{ state.npcVitality }%</span>
          </div>
          <div class="cmodal-target-strip" if={ state.foes.length > 1 } onclick={ handleTargetAction }>
            <button
              each={ foe in state.foes }
              type="button"
              class="cmodal-target-chip { state.activeTargetName === foe.name ? 'active' : '' }"
              data-action="select-target"
              data-target={ foe.name }
              disabled={ state.busy || foe.vitality <= 0 }
            >
              <span>{ foe.name.toUpperCase() }</span>
              <small>{ foe.vitality }%</small>
            </button>
          </div>
        </div>

        <div class="cmodal-vs">VS</div>

        <!-- Player -->
        <div class="cmodal-actor player">
          <div class="cmodal-actor-name">{ state.playerLabel }</div>
          <div class="cmodal-vitals-container">
            <div class="cmodal-bar player">
              <div class="cmodal-fill { vitalTone(state.playerVitality, state.playerVitalityMax) }" style="width:{ vitalWidth(state.playerVitality, state.playerVitalityMax) }%"></div>
            </div>
            <span class="cmodal-pct">{ vitalLabel(state.playerVitality, state.playerVitalityMax) }</span>
          </div>
          <div class="cmodal-vitals-mini">
            <div class="cmodal-mini-row">
              <span class="mini-label spr">SPR</span>
              <div class="cmodal-bar mini spr"><div class="cmodal-fill spr" style="width:{ vitalWidth(state.playerSpirit, state.playerSpiritMax) }%"></div></div>
              <span class="cmodal-pct-sm">{ vitalLabel(state.playerSpirit, state.playerSpiritMax) }</span>
            </div>
            <div class="cmodal-mini-row">
              <span class="mini-label ess">ESS</span>
              <div class="cmodal-bar mini ess"><div class="cmodal-fill ess" style="width:{ vitalWidth(state.playerEssence, state.playerEssenceMax) }%"></div></div>
              <span class="cmodal-pct-sm">{ vitalLabel(state.playerEssence, state.playerEssenceMax) }</span>
            </div>
          </div>
          <!-- Companion bars -->
          <div each={ ally in state.allyBars } class="cmodal-ally-bar">
            <span class="ally-bar-name">{ ally.name }</span>
            <div class="cmodal-ally-vitals">
              <div class="cmodal-bar ally">
                <div class="cmodal-fill { vitalTone(ally.vitality, ally.maxVitality) }" style="width:{ vitalWidth(ally.vitality, ally.maxVitality) }%"></div>
              </div>
              <div class="cmodal-bar mini spr"><div class="cmodal-fill spr" style="width:{ vitalWidth(ally.spirit, ally.maxSpirit) }%"></div></div>
              <div class="cmodal-bar mini ess"><div class="cmodal-fill ess" style="width:{ vitalWidth(ally.essence, ally.maxEssence) }%"></div></div>
            </div>
            <span class="cmodal-pct-sm">{ vitalLabel(ally.vitality, ally.maxVitality) }</span>
          </div>
        </div>
      </div>

      <!-- Initiative Strip -->
      <div class="cmodal-initiative" if={ state.initiative.length }>
        <span class="cmodal-label">TURN ORDER · ROUND { state.round }</span>
        <div class="cmodal-init-strip">
          <div each={ name in state.initiative } class="cmodal-init-token { isYouToken(name) ? 'you' : 'npc' } { isActiveTurnToken(name) ? 'active-turn' : '' }">
            { displayInitiativeName(name) }
          </div>
        </div>
      </div>

      <div class="cmodal-participants" if={ state.participants.length } onclick={ handleParticipantAction }>
        <span class="cmodal-label">PARTICIPANTS</span>
        <div class="cmodal-participant-grid">
          <button
            each={ p in state.participants }
            type="button"
            class="cmodal-participant { p.side } tooltip-wrap"
            data-action="inspect-participant"
            data-participant-id={ p.id }
          >
            <div class="cp-head">
              <span>{ p.name }</span>
              <small>{ p.sideLabel }</small>
            </div>
            <div class="cp-vitals">
              <span>VIT { p.vitals.vitality }</span>
              <span>SPR { p.vitals.spirit }</span>
              <span>ESS { p.vitals.essence }</span>
            </div>
            <div class="tooltip" data-position="top" data-tip-custom="true">
              <raw-html content={ p.hoverDetail.replace(/\n/g, '<br/>') } />
            </div>
          </button>
        </div>
      </div>

      <div class="cmodal-impact { state.impactTone }" if={ state.lastImpact }>
        <span class="impact-label">{ state.lastImpactLabel }</span>
        <span class="impact-text">{ state.lastImpact }</span>
      </div>

      <!-- Combat Log -->
      <div class="cmodal-log-section">
        <div class="cmodal-label">COMBAT LOG</div>
        <div class="cmodal-log" if={ state.log.length }>
          <div each={ entry in state.log } class="cmodal-log-entry { entry.actor }">
            <span class="clog-round">R{ entry.round }</span>
            <span class="clog-who">
              { entry.actor === 'player'
                ? state.playerLabel
                : entry.actor === 'companion'
                  ? (entry.actorName || 'ALLY').toUpperCase()
                  : (entry.actorName || state.npcName).toUpperCase()
              }:
            </span>
            <span class="clog-act">{ entry.action }</span>
            <span class="clog-txt">{ entry.outcome }</span>
          </div>
        </div>
        <div class="cmodal-log-empty" if={ !state.log.length }>Battle has just begun. Ready your weapon.</div>
      </div>

      <!-- Action Panel -->
      <div class="cmodal-actions" if={ !state.outcome } onclick={ handleActionClick }>
        <div class="cmodal-primary-actions">
          <button class="cmodal-btn attack" data-action="attack" disabled={ state.busy }>
            <span class="cbtn-icon">⚔</span> ATTACK { state.activeTargetName.toUpperCase() }
          </button>
          <button each={ ability in state.readyAbilities } class="cmodal-btn" data-action="open-ability" data-ability-name={ ability.name } disabled={ state.busy }>
            <span class="cbtn-icon">✦</span> { ability.name.toUpperCase() }
          </button>
          <button class="cmodal-btn" data-action="flee" disabled={ state.busy }>
            <span class="cbtn-icon">🏃</span> FLEE
          </button>
        </div>

        <div class="cmodal-item-actions" if={ state.combatItems.length }>
          <button each={ item in state.combatItems } class="cmodal-btn item" data-action="use-item" data-item-name={ item.name } disabled={ state.busy }>
            <span class="cbtn-icon">{ item.equipped ? '⭐' : '🎒' }</span> { item.name.toUpperCase() }
          </button>
        </div>

        <div class="cmodal-custom-input">
          <input
            type="text"
            placeholder="Describe a unique combat action..."
            disabled={ state.busy }
            onkeydown={ handleCustomInputKey }
          >
          <button type="button" class="cmodal-btn primary" data-action="send-custom" disabled={ state.busy }>SEND</button>
        </div>
      </div>

      <!-- Wizard Overlay (BG3 Style) -->
      <div class="cmodal-wizard { state.wizardPhase }" if={ state.wizardOpen }>
        <div class="wizard-box">
          <div class="wizard-header">
            <h3>{ state.wizardTitle }</h3>
            <p if={ state.wizardPhase === 'ready' }>{ state.wizardChance }% CHANCE OF SUCCESS</p>
          </div>
          <p class="wizard-skill-note" if={ state.wizardSkillBonus > 0 }>
            includes +{ state.wizardSkillBonus } from { state.wizardSkillName.toUpperCase() }
          </p>
          <p class="wizard-hint" if={ state.wizardPhase === 'ready' }>
            D20 check directly drives success/failure for this action.
          </p>
          <div class="wizard-mechanics">
            <div class="mech-item">
              <span class="mech-label">TARGET DC</span>
              <span class="mech-val">{ state.wizardDC }</span>
            </div>
            <div class="mech-plus">vs</div>
            <div class="mech-item">
              <span class="mech-label">{ state.wizardStat } BONUS</span>
              <span class="mech-val tooltip-wrap">
                { state.wizardBonus >= 0 ? '+' : '' }{ state.wizardBonus }
                <div class="tooltip" data-position="top" if={ state.wizardBonusDetail } data-tip-custom="true">
                  { state.wizardBonusDetail }
                </div>
              </span>
            </div>
          </div>
          <div class="wizard-bonus-breakdown tooltip-wrap" if={ state.wizardBonusSummary && state.wizardPhase === 'ready' }>
            { state.wizardBonusSummary }
            <div class="tooltip" data-position="top" if={ state.wizardBonusDetail } data-tip-custom="true">
              { state.wizardBonusDetail }
            </div>
          </div>

          <!-- Dice Container -->
          <div class="wizard-dice-container">
            <div class="d20-die { state.isRolling ? 'rolling' : '' } { state.wizardPhase === 'result' ? 'stopped' : '' }">
              <div class="die-face">{ state.isRolling ? '?' : state.lastRoll || '20' }</div>
            </div>
            <div class="wizard-roll-result" if={ state.wizardPhase === 'result' }>
              <div class="result-math">{ state.lastRoll } + { state.wizardBonus } = { state.lastRoll + state.wizardBonus }</div>
              <div class="result-verdict { (state.lastRoll + state.wizardBonus >= state.wizardDC) ? 'success' : 'fail' }">
                { (state.lastRoll + state.wizardBonus >= state.wizardDC) ? 'SUCCESS' : 'FAILURE' }
              </div>
            </div>
          </div>

          <div class="wizard-footer" onclick={ handleWizardAction }>
            <button class="cmodal-btn primary" if={ state.wizardPhase === 'ready' } data-action="start-roll">ROLL D20</button>
            <button class="cmodal-btn" if={ state.wizardPhase === 'result' } data-action="finalize-wizard">CONTINUE</button>
          </div>
        </div>
      </div>

      <!-- NPC Turn Overlay -->
      <div class="cmodal-wizard npc-turn" if={ state.npcTurnOpen }>
        <div class="wizard-box">
          <div class="wizard-header">
            <h3 style="color:var(--danger)">{ state.npcName.toUpperCase() } ATTACKS!</h3>
            <p>BRACE YOURSELF...</p>
          </div>
          <div class="wizard-dice-container">
            <div class="d20-die rolling npc">
              <div class="die-face">?</div>
            </div>
          </div>
          <div class="wizard-footer">
             <p class="pulsing-text">DEFENDING...</p>
          </div>
        </div>
      </div>

      <!-- Outcome Overlays -->
      <div class="cmodal-outcomes" onclick={ handleOutcomeAction }>
        <div class="cmodal-overlay victory" if={ state.outcome === 'victory' }>
          <div class="overlay-content">
            <div class="overlay-icon">🏆</div>
            <h2>VICTORY</h2>
            <p>{ state.npcName } has been defeated.</p>
            <button type="button" class="cmodal-btn" data-action="close">CONTINUE</button>
          </div>
        </div>

        <div class="cmodal-overlay defeat" if={ state.outcome === 'defeat' }>
          <div class="overlay-content">
            <div class="overlay-icon">💀</div>
            <h2>DEFEAT</h2>
            <p>You have fallen in battle.</p>
            <button type="button" class="cmodal-btn" data-action="close">CONTINUE (GHOST MODE)</button>
          </div>
        </div>
      </div>

    </div>
  </dialog>

  <style>
    .combat-modal {
      background: rgba(2, 4, 8, var(--modal-backdrop-alpha));
      border: none;
      padding: 0;
      margin: 0;
      width: 100vw;
      height: 100dvh;
      max-width: none;
      max-height: none;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-prose);
      font-family: var(--font-mono);
    }

    .cmodal-content {
      background: var(--surface-card-2);
      border: 1px solid color-mix(in srgb, var(--gold) var(--alpha-focus), var(--border));
      width: min(95vw, 43.75rem);
      max-height: min(90dvh, 58rem);
      padding: var(--space-lg);
      position: relative;
      box-shadow: var(--shadow-elev-3);
      border-radius: var(--radius-lg);
      overflow-y: auto;
    }

    .cmodal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid var(--border);
      padding-bottom: var(--space-md);
      margin-bottom: var(--space-lg);
    }

    .cmodal-title-group {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      font-weight: bold;
    }

    .cmodal-icon { color: var(--gold); }
    .cmodal-round { color: var(--text-dim); font-size: var(--type-xs); }
    .cmodal-close {
      border: 1px solid var(--border);
      background: var(--surface-card-3);
      color: var(--text-dim);
      width: 1.8rem;
      height: 1.8rem;
      border-radius: var(--radius-sm);
      cursor: pointer;
      font-family: inherit;
      transition:
        border-color var(--motion-fast) var(--motion-ease-standard),
        color var(--motion-fast) var(--motion-ease-standard),
        background var(--motion-fast) var(--motion-ease-standard);
    }
    .cmodal-close:hover {
      border-color: var(--gold);
      color: var(--gold);
      background: var(--gold-glow);
    }
    .cmodal-close[aria-disabled='true'] {
      opacity: 0.45;
      cursor: default;
    }
    .cmodal-close[aria-disabled='true']:hover {
      border-color: var(--border);
      color: var(--text-dim);
      background: var(--surface-card-3);
    }

    .cmodal-combatants {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--space-lg);
      gap: var(--space-md);
    }

    .cmodal-actor {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .cmodal-actor.player { align-items: flex-end; }
    .cmodal-actor-name {
      font-weight: bold;
      font-size: var(--type-xs);
      letter-spacing: var(--tracking-wide);
      color: var(--text-muted);
    }

    .cmodal-vitals-container {
      width: 100%;
      display: flex;
      align-items: center;
      gap: var(--space-sm);
    }
    .player .cmodal-vitals-container { flex-direction: row-reverse; }

    .cmodal-bar {
      flex: 1;
      height: 0.875rem;
      background: var(--bg);
      position: relative;
      border-radius: var(--radius-sm);
      overflow: hidden;
      border: 1px solid var(--border);
    }

    .cmodal-fill {
      height: 100%;
      transition: width var(--motion-slow) var(--motion-ease-press);
    }
    .cmodal-fill.high { background: var(--sem-success-fg); }
    .cmodal-fill.mid  { background: var(--sem-warn-fg); }
    .cmodal-fill.low  { background: var(--danger); }

    .cmodal-pct {
      font-size: var(--type-xs);
      font-weight: bold;
      width: 2.8125rem;
      text-align: right;
    }
    .player .cmodal-pct { text-align: left; }

    .cmodal-ally-bar {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
      width: 100%;
    }
    .ally-bar-name {
      font-size: var(--type-3xs);
      color: var(--sem-info-fg);
      letter-spacing: var(--tracking-wide);
      min-width: 3.125rem;
      text-align: right;
    }
    .cmodal-bar.ally { flex: 1; height: 0.5rem; }
    .cmodal-pct-sm {
      font-size: var(--type-3xs);
      font-weight: bold;
      width: 2.1875rem;
      text-align: left;
      color: var(--text-muted);
    }

    .cmodal-vitals-mini {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
      width: 100%;
    }
    .cmodal-mini-row {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
    }
    .player .cmodal-mini-row { flex-direction: row-reverse; }
    .mini-label {
      font-size: var(--type-3xs);
      font-weight: bold;
      letter-spacing: 0.06em;
      width: 1.75rem;
      text-align: right;
    }
    .player .mini-label { text-align: left; }
    .mini-label.spr { color: var(--brand-primary); }
    .mini-label.ess { color: var(--sem-warn-fg); }
    .cmodal-bar.mini { flex: 1; height: 0.375rem; border-radius: var(--radius-sm); }
    .cmodal-bar.mini.spr { border-color: color-mix(in srgb, var(--brand-primary) var(--alpha-mid), var(--border)); }
    .cmodal-bar.mini.ess { border-color: color-mix(in srgb, var(--gold) var(--alpha-mid), var(--border)); }
    .cmodal-fill.spr { height: 100%; background: var(--brand-primary); transition: width var(--motion-slow) var(--motion-ease-press); }
    .cmodal-fill.ess { height: 100%; background: var(--gold); transition: width var(--motion-slow) var(--motion-ease-press); }

    .cmodal-ally-vitals {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      flex: 1;
    }

    .cmodal-target-strip {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
    }

    .cmodal-target-chip {
      border: 1px solid var(--border);
      background: var(--surface-card-3);
      color: var(--text-muted);
      border-radius: var(--radius-sm);
      padding: 0.18rem var(--space-xs);
      font-size: var(--type-3xs);
      letter-spacing: var(--tracking-wide);
      display: inline-flex;
      gap: var(--space-xs);
      align-items: baseline;
      font-family: inherit;
      cursor: pointer;
    }

    .cmodal-target-chip small {
      color: var(--text-dim);
      font-size: var(--type-3xs);
    }

    .cmodal-target-chip.active {
      border-color: var(--gold);
      color: var(--gold);
      background: color-mix(in srgb, var(--gold) var(--alpha-low), var(--surface-card-3));
    }

    .cmodal-target-chip:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .cmodal-vs {
      padding: 0 var(--space-xs);
      font-weight: bold;
      color: var(--gold);
      opacity: 0.3;
      font-size: 1.2rem;
    }

    .cmodal-initiative,
    .cmodal-participants,
    .cmodal-impact {
      margin-bottom: var(--space-md);
      background: color-mix(in srgb, var(--surface-card-3) var(--alpha-high), transparent);
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-high), transparent);
      border-radius: var(--radius-sm);
      padding: var(--space-sm);
    }

    .cmodal-label {
      font-size: var(--type-3xs);
      font-weight: bold;
      color: var(--text-dim);
      margin-bottom: var(--space-xs);
      display: block;
      letter-spacing: 0.1em;
    }

    .cmodal-init-strip {
      display: flex;
      gap: var(--space-xs);
      flex-wrap: wrap;
    }

    .cmodal-init-token {
      font-size: var(--type-xs);
      padding: var(--space-xs) 0.6rem;
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
      background: var(--surface-card-3);
    }
    .cmodal-init-token.you {
      border-color: var(--gold);
      color: var(--gold);
      background: color-mix(in srgb, var(--gold) var(--alpha-low), var(--surface-card-3));
    }
    .cmodal-init-token.active-turn {
      box-shadow: 0 0 0 1px color-mix(in srgb, var(--brand-primary) var(--alpha-ghost), transparent);
      background: color-mix(in srgb, var(--brand-primary) var(--alpha-low), var(--surface-card-3));
      border-color: var(--brand-primary);
      color: var(--sem-info-fg);
    }

    .cmodal-participant-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(9.5rem, 1fr));
      gap: var(--space-xs);
    }
    .cmodal-participant {
      border: 1px solid var(--border);
      background: var(--surface-card-3);
      color: var(--text-prose);
      border-radius: var(--radius-sm);
      padding: var(--space-xs);
      text-align: left;
      cursor: pointer;
      font-family: inherit;
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
      transition:
        border-color var(--motion-fast) var(--motion-ease-standard),
        transform var(--motion-fast) var(--motion-ease-standard),
        background var(--motion-fast) var(--motion-ease-standard);
    }
    .cmodal-participant.player { border-color: color-mix(in srgb, var(--gold) var(--alpha-focus), var(--border)); }
    .cmodal-participant.ally { border-color: color-mix(in srgb, var(--brand-primary) var(--alpha-mid), var(--border)); }
    .cmodal-participant.foe { border-color: color-mix(in srgb, var(--danger) var(--alpha-mid), var(--border)); }
    .cmodal-participant:hover { border-color: var(--brand-primary); transform: translateY(-1px); }
    .cp-head {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: var(--type-3xs);
      letter-spacing: var(--tracking-wide);
      font-weight: bold;
    }
    .cp-head small {
      color: var(--text-dim);
      font-size: var(--type-3xs);
    }
    .cp-vitals {
      display: flex;
      gap: var(--space-xs);
      flex-wrap: wrap;
      font-size: var(--type-3xs);
      color: var(--text-muted);
    }

    .cmodal-log-section {
      margin-bottom: var(--space-lg);
    }

    .cmodal-log {
      background: var(--surface-card-3);
      padding: var(--space-md);
      border: 1px solid var(--border);
      min-height: 6.25rem;
      max-height: 8.75rem;
      overflow-y: auto;
      font-size: var(--type-xs);
      border-radius: var(--radius-sm);
    }

    .cmodal-log-entry { margin-bottom: var(--space-xs); line-height: 1.4; border-left: 0.125rem solid transparent; padding-left: var(--space-xs); }
    .cmodal-log-entry.player { border-left-color: var(--gold); }
    .cmodal-log-entry.npc { color: var(--text-dim); border-left-color: var(--text-dim); }
    .clog-round { color: var(--text-dim); margin-right: var(--space-sm); font-size: var(--type-xs); }
    .clog-who { font-weight: bold; margin-right: var(--space-xs); color: var(--text-prose); }
    .clog-act { color: var(--text-muted); margin-right: var(--space-xs); }
    .clog-act::after { content: '→'; margin-left: var(--space-xs); color: var(--text-dim); }

    .cmodal-log-empty {
      color: var(--text-dim);
      font-style: italic;
      font-size: var(--type-xs);
      padding: var(--space-md);
    }

    .cmodal-impact {
      display: flex;
      gap: 0.65rem;
      align-items: baseline;
    }
    .cmodal-impact.danger { border-color: var(--sem-danger-border); background: var(--sem-danger-bg); }
    .cmodal-impact.success { border-color: var(--sem-success-border); background: var(--sem-success-bg); }
    .impact-label {
      font-size: var(--type-3xs);
      letter-spacing: 0.1em;
      color: var(--text-dim);
      font-weight: bold;
      white-space: nowrap;
    }
    .impact-text {
      font-size: var(--type-xs);
      color: var(--text-prose);
      line-height: 1.35;
    }

    .cmodal-actions {
      display: flex;
      flex-direction: column;
      gap: var(--space-md);
    }

    .cmodal-primary-actions {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(8.75rem, 1fr));
      gap: var(--space-sm);
    }

    .cmodal-item-actions {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(11.25rem, 1fr));
      gap: var(--space-xs);
    }

    .cmodal-btn {
      background: var(--surface-card-1);
      border: 1px solid var(--border);
      color: var(--text-prose);
      padding: 0.85rem var(--space-md);
      cursor: pointer;
      font-family: inherit;
      font-weight: bold;
      font-size: var(--type-xs);
      transition:
        background var(--motion-fast) var(--motion-ease-standard),
        border-color var(--motion-fast) var(--motion-ease-standard),
        color var(--motion-fast) var(--motion-ease-standard),
        transform var(--motion-fast) var(--motion-ease-standard),
        box-shadow var(--motion-fast) var(--motion-ease-standard);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-xs);
      border-radius: var(--radius-sm);
    }
    .cmodal-btn:hover:not(:disabled) {
      background: var(--surface-raised);
      border-color: var(--gold);
      transform: translateY(-1px);
      box-shadow: var(--shadow-elev-1);
    }
    .cmodal-btn:active:not(:disabled) { transform: translateY(0); }
    .cmodal-btn:disabled { opacity: 0.4; cursor: not-allowed; }

    .cmodal-btn.attack {
      border-color: var(--sem-danger-border);
      color: var(--sem-danger-fg);
      background: color-mix(in srgb, var(--sem-danger-bg) var(--alpha-high), var(--surface-card-1));
    }
    .cmodal-btn.attack:hover:not(:disabled) {
      background: color-mix(in srgb, var(--sem-danger-bg) 90%, var(--surface-card-1));
      border-color: var(--danger);
    }
    .cmodal-btn.item {
      border-color: var(--sem-info-border);
      color: var(--sem-info-fg);
      background: color-mix(in srgb, var(--sem-info-bg) var(--alpha-high), var(--surface-card-1));
    }
    .cmodal-btn.item:hover:not(:disabled) {
      background: color-mix(in srgb, var(--sem-info-bg) 90%, var(--surface-card-1));
      border-color: var(--brand-primary);
    }
    .cmodal-btn.primary {
      background: var(--gold);
      color: var(--bg);
      border-color: var(--gold);
    }
    .cmodal-btn.primary:hover:not(:disabled) {
      background: color-mix(in srgb, var(--gold) 82%, #fff);
      border-color: color-mix(in srgb, var(--gold) 82%, #fff);
    }

    .cmodal-custom-input {
      display: flex;
      gap: var(--space-xs);
      margin-top: var(--space-xs);
    }

    .cmodal-custom-input input {
      flex: 1;
      background: var(--surface-card-3);
      border: 1px solid var(--border);
      color: var(--text-prose);
      padding: var(--space-sm) var(--space-md);
      font-family: inherit;
      border-radius: var(--radius-sm);
      outline: none;
      &:focus { border-color: var(--gold); }
    }
    .cmodal-custom-input .cmodal-btn { flex-shrink: 0; }

    .cmodal-wizard {
      position: fixed;
      inset: 0;
      background: color-mix(in srgb, var(--bg) 62%, transparent);
      backdrop-filter: blur(0.125rem);
      z-index: 3;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.3s ease;
    }

    .wizard-box {
      width: 90%;
      max-width: 25rem;
      background: color-mix(in srgb, var(--surface-card-2) 90%, black);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      padding: 1.1rem var(--space-md);
      box-shadow: var(--shadow-elev-2);
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: var(--space-lg);
    }

    .wizard-header h3 { margin: 0; font-size: 1.4rem; color: var(--gold); letter-spacing: 0.1em; }
    .wizard-header p { margin: var(--space-xs) 0 0; font-size: var(--type-xs); color: var(--text-dim); font-weight: bold; }
    .wizard-skill-note {
      margin: -1.25rem 0 0;
      font-size: var(--type-3xs);
      color: var(--text-dim);
      letter-spacing: var(--tracking-wide);
      text-transform: uppercase;
    }
    .wizard-hint {
      margin: -1.5rem 0 0;
      font-size: var(--type-3xs);
      color: var(--text-dim);
      letter-spacing: var(--tracking-wide);
      text-transform: uppercase;
    }
    .wizard-bonus-breakdown {
      margin: -1rem 0 0;
      padding: var(--space-xs) 0.6rem;
      font-size: var(--type-3xs);
      color: var(--text-muted);
      letter-spacing: 0.04em;
      text-transform: uppercase;
      text-align: center;
      background: color-mix(in srgb, var(--text-dim) 8%, transparent);
      border: 1px solid color-mix(in srgb, var(--text-dim) 12%, transparent);
      border-radius: var(--radius-sm);
      cursor: help;
    }

    .wizard-mechanics {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-lg);
    }

    .mech-item { display: flex; flex-direction: column; gap: var(--space-xs); }
    .mech-label { font-size: var(--type-3xs); color: var(--text-dim); font-weight: bold; }
    .mech-val { font-size: var(--type-lg); font-weight: bold; color: var(--text-prose); }
    .mech-plus { font-size: 1.2rem; color: var(--text-dim); }

    .wizard-dice-container {
      height: 10rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-md);
    }

    .d20-die {
      width: 6.25rem;
      height: 6.25rem;
      background: var(--surface-card-3);
      border: 0.1875rem solid var(--gold);
      clip-path: polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2.5rem;
      font-weight: bold;
      color: var(--gold);
      box-shadow: 0 0 1.25rem color-mix(in srgb, var(--gold) var(--alpha-mid), transparent);
    }

    .d20-die.rolling { animation: die-shake 0.15s infinite alternate; background: var(--surface-raised); }
    .d20-die.npc {
      border-color: var(--danger);
      color: var(--danger);
      box-shadow: 0 0 1.25rem color-mix(in srgb, var(--danger) var(--alpha-mid), transparent);
    }


    .wizard-roll-result {
      animation: result-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    }


    .result-math { font-size: var(--type-md); color: var(--text-dim); margin-bottom: var(--space-xs); }
    .result-verdict { font-size: var(--type-lg); font-weight: bold; letter-spacing: 0.2em; }
    .result-verdict.success { color: var(--sem-success-fg); text-shadow: 0 0 0.9375rem color-mix(in srgb, var(--sem-success-fg) var(--alpha-mid), transparent); }
    .result-verdict.fail { color: var(--danger); text-shadow: 0 0 0.9375rem color-mix(in srgb, var(--danger) var(--alpha-mid), transparent); }

    .pulsing-text {
      animation: pulse-opacity 1s infinite;
      color: var(--danger);
      font-weight: bold;
      letter-spacing: 0.2em;
      font-size: var(--type-xs);
    }

    .cmodal-overlay {
      position: fixed;
      inset: 0;
      background: color-mix(in srgb, var(--bg) 96%, transparent);
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      z-index: 4;
    }

    .overlay-content h2 { margin: var(--space-md) 0; font-size: 2.5rem; letter-spacing: var(--tracking-wide); }
    .overlay-content p { color: var(--text-dim); margin-bottom: var(--space-lg); font-size: var(--type-lg); }
    .overlay-icon { font-size: 4rem; }

    .victory h2 { color: var(--gold); }
    .defeat h2 { color: var(--danger); }


    @media (max-width: 37.5rem) {
      .cmodal-content { width: 100%; height: 100%; max-height: 100dvh; max-width: none; border: none; border-radius: 0; padding: var(--space-lg); }
      .cmodal-combatants { flex-direction: column; gap: var(--space-md); }
      .cmodal-vs { display: none; }
      .cmodal-actor.player { align-items: flex-start; }
      .player .cmodal-vitals-container { flex-direction: row; }
      .player .cmodal-pct { text-align: right; }
    }
  </style>

  <script>
    import { dispatcher, sysNotify } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../game-store.js'
    import { resolvePreferredCombatItemAction } from '../../engine/item-action-model.ts'
    import { describeVitalScalar } from '../../engine/vitals'
    import { buildInfoDialog } from '../dialog/dialog-openers.ts'
    import { buildActionTarget, buildToolRef } from '../helpers/action-refs.ts'
    import { buildAbilityModalActions } from '../helpers/ability-actions.ts'
    import { playEffect } from '../../engine/audio.js'
    import { 
      deriveCombatUiState, 
      buildImpactSummary, 
      buildParticipants, 
      getCombatFoes, 
      resolveActiveTargetName, 
      peekTurnActor,
      getCombatItems,
      getReadyAbilities
    } from '../../engine/combat-logic.ts'

    export default {
      state: {
        npcName:        '',
        npcVitality:    100,
        foes:           [],
        activeTargetName: '',
        playerVitality: 100,
        playerVitalityMax: 100,
        playerSpirit:   100,
        playerSpiritMax: 100,
        playerEssence:  100,
        playerEssenceMax: 100,
        round:          1,
        initiative:     [],
        turnActor:      '',
        log:            [],
        readyAbilities: [],
        combatItems:    [],
        participants:   [],
        allyBars:       [],
        outcome:        null,
        busy:           false,
        playerLabel:    'YOU',

        // Wizard states
        wizardOpen:     false,
        wizardPhase:    'ready', // 'ready' | 'rolling' | 'result'
        wizardTitle:    '',
        wizardDC:       10,
        wizardBonus:    0,
        wizardStat:     '',
        wizardChance:   50,
        wizardSkillBonus: 0,
        wizardSkillName: 'skill',
        wizardBonusSummary: '',
        wizardBonusDetail: '',
        isRolling:      false,
        lastRoll:       0,

        // NPC turn
        npcTurnOpen:    false,
        lastImpact:     '',
        lastImpactLabel:'',
        impactTone:     'neutral',
      },

      onBeforeUpdate(props) {
        if (props.active === false && this.$('dialog')?.open) {
          this.closeModal()
        }
      },

      onMounted() {
        const dialog = this.$('dialog')
        if (dialog) {
          dialog.addEventListener('cancel', (e) => {
            e.preventDefault()
            this.requestClose()
          })
        }

        this.handles = [
          dispatcher.on(TOPICS.combat.started, (_e, d) => {
            playEffect('ui_open')
            const gs = gameStore.state
            const derived = deriveCombatUiState(gs, this.state)
            this.update({
              ...derived,
              npcName:        derived.npcName || d?.npc?.name || '???',
              readyAbilities: getReadyAbilities(gs),
              log:            [],
              outcome:        null,
              busy:           false,
              wizardOpen:     false,
              npcTurnOpen:    false,
              lastImpact:     '',
              lastImpactLabel:'',
              impactTone:     'neutral',
            })
            const dialogRef = this.$('dialog')
            dialogRef?.showModal()
            dispatcher.trigger('combat-modal', TOPICS.ui.overlayOpened, { key: 'combat-modal' })
          }),

          dispatcher.on(TOPICS.ui.diceRollRequired, (_e, d) => {
             playEffect('ui_open')
             this.update({
               wizardOpen:   true,
               wizardPhase:  'ready',
               wizardTitle:  d.title || 'ACTION CHECK',
               wizardDC:     d.dc || 10,
               wizardBonus:  d.bonus || 0,
               wizardStat:   d.primaryStat || 'STAT',
               wizardChance: d.chance || 50,
               wizardSkillBonus: d.skillBonus || 0,
               wizardSkillName: d.skillName || 'skill',
               wizardBonusSummary: d.bonusSummary || '',
               wizardBonusDetail: d.bonusDetail || '',
               isRolling:    false,
               lastRoll:     0,
               busy:         true,
             })
             const dialog = this.$('dialog')
             if (dialog && !dialog.open) {
               dialog.showModal()
               dispatcher.trigger('combat-modal', TOPICS.ui.overlayOpened, { key: 'combat-modal' })
             }
          }),

          dispatcher.on(TOPICS.combat.npcTurnStart, () => {
             playEffect('danger')
             this.update({ npcTurnOpen: true, wizardOpen: false })
          }),

          dispatcher.on(TOPICS.combat.turnEnd, (_e, d) => {
            const gs = gameStore.state
            const derived = deriveCombatUiState(gs, this.state)
            
            const prevPlayerVitality = this.state.playerVitality
            const prevNpcVitality = this.state.npcVitality
            const nextPlayerVitality = derived.playerVitality ?? prevPlayerVitality
            const nextNpcVitality = derived.npcVitality ?? prevNpcVitality
            
            const impact = buildImpactSummary(
              d,
              nextPlayerVitality - prevPlayerVitality,
              nextNpcVitality - prevNpcVitality,
              derived.activeTargetName,
              derived.npcName
            )

            this.update({
              ...derived,
              readyAbilities: getReadyAbilities(gs),
              busy:           !!gameStore.isProcessing,
              npcTurnOpen:    false,
              wizardOpen:     false,
              lastImpact:     impact.text,
              lastImpactLabel: impact.label,
              impactTone:     impact.tone,
            })
          }),

          dispatcher.on(TOPICS.combat.targetChanged, (_e, data) => {
            const foes = getCombatFoes({ foes: data?.foes }, this.state)
            const activeTargetName = resolveActiveTargetName(foes, data?.targetName ?? this.state.activeTargetName)
            const activeFoe = foes.find(foe => foe.name === activeTargetName)
            this.update({
              foes,
              activeTargetName,
              npcName: activeTargetName || this.state.npcName,
              npcVitality: activeFoe?.vitality ?? this.state.npcVitality,
              participants: buildParticipants(gameStore.state, foes),
              turnActor: peekTurnActor(gameStore.state?.combat) ?? this.state.turnActor,
            })
          }),

          dispatcher.on(TOPICS.combat.ended, (_e, d) => {
            const outcome = d?.victory ? 'victory' : 'defeat'
            this.update({ outcome, busy: false, npcTurnOpen: false, wizardOpen: false })
          }),

          dispatcher.on(TOPICS.combat.fled, () => {
            this.closeModal()
          }),
          dispatcher.on(TOPICS.combat.closeRequested, () => {
            this.closeModal()
          }),

          dispatcher.on(TOPICS.game.actionStart,    () => { this.update({ busy: true }) }),
          dispatcher.on(TOPICS.game.actionComplete, () => {
            const gs = gameStore.state
            const foes = getCombatFoes(gs?.combat, this.state)
            this.update({
              busy: !!gameStore.isProcessing,
              combatItems: getCombatItems(gs),
              participants: buildParticipants(gs, foes),
            })
          }),
          dispatcher.on(TOPICS.game.actionFailed,   (_e, data) => { 
            this.update({ busy: false, wizardOpen: false, npcTurnOpen: false })
            if (data?.cancelled) {
              sysNotify('Action cancelled.')
            }
          }),
        ]
      },

      onUnmounted() {
        this.handles?.forEach(h => dispatcher.off(h))
      },

      inspectParticipant(participant) {
        if (!participant) return
        playEffect('ui_click')
        dispatcher.trigger('combat-modal', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: participant.name,
            desc: participant.detail || participant.hoverDetail || 'No intel.',
            meta: `COMBAT ${participant.sideLabel || 'ENTITY'}`,
          },
        }))
      },

      isYouToken(name) {
        const token = String(name ?? '').trim().toLowerCase()
        if (token === 'player' || token === 'you') return true
        const playerName = String(gameStore.state?.player?.name ?? gameStore.state?.player?.identity ?? '').trim().toLowerCase()
        return !!playerName && token === playerName
      },

      vitalWidth(value, max) {
        return describeVitalScalar(value, max).percent
      },

      vitalLabel(value, max) {
        return describeVitalScalar(value, max).label
      },

      vitalTone(value, max) {
        return describeVitalScalar(value, max).tone
      },

      displayInitiativeName(name) {
        if (this.isYouToken(name)) return this.state.playerLabel
        return String(name ?? 'ALLY').toUpperCase()
      },

      isActiveTurnToken(name) {
        const token = String(name ?? '').trim().toLowerCase()
        const active = String(this.state.turnActor ?? '').trim().toLowerCase()
        return !!token && !!active && token === active
      },

      closeModal() {
        playEffect('ui_close')
        this.$('dialog')?.close()
        dispatcher.trigger('combat-modal', TOPICS.ui.overlayClosed, { key: 'combat-modal' })
        this.update({
          outcome: null,
          wizardOpen: false,
          npcTurnOpen: false,
          lastImpact: '',
          lastImpactLabel: '',
          impactTone: 'neutral',
        })
      },

      onBackdrop(e) {
        if (e.target !== e.currentTarget) return
        e.stopPropagation()
        this.requestClose()
      },

      requestClose() {
        if (!this.state.outcome) {
          const now = Date.now()
          if (!this.blockedCloseAt || (now - this.blockedCloseAt) > 1200) {
            this.blockedCloseAt = now
            sysNotify('Combat is active. Resolve the encounter or flee before closing.')
          }
          return
        }
        this.closeModal()
      },

      attackNpc() {
        if (!this.state.npcName || this.state.busy) return
        playEffect('ui_click')
        const target = this.state.activeTargetName || this.state.npcName
        gameStore.handleAction(`Attack ${target}`, undefined, {
          source: 'player',
          category: 'attack',
          tool: buildToolRef('attack', 'attack', 'Attack'),
          explicitTargets: [buildActionTarget('npc', target)],
          tags: ['combat_modal', 'combat_attack'],
        })
      },

      openAbilityModal(ability) {
        if (this.state.busy) return
        if (!ability?.name) return
        const gs = gameStore.state
        if (!gs) return
        const resolvedArgs = gameStore.getResolvedAbilityArgs(gs, ability)
        const requiresInput = resolvedArgs.length > 0
        const targets = gs?.currentRoom?.npcs?.map(n => n.name) ?? []
        if (gs?.player?.identity) targets.unshift(gs.player.identity)
        const actions = buildAbilityModalActions(gs, ability, {
          resolvedArgs,
          targets,
          title: ability.name.toUpperCase(),
          contextTag: 'combat_ability',
          preferredTargetName: this.state.activeTargetName || this.state.npcName,
        })

        dispatcher.trigger('combat-modal', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: ability.name,
            desc: [
              ability.desc || 'No description.',
              '',
              `**COOLDOWN**\n${ability.cooldown} game-minutes`,
              '',
              requiresInput
                ? '**WHEN USED**\nYou will choose any needed details in a quick follow-up step.'
                : '**WHEN USED**\nNo extra details required.',
            ].join('\n'),
            meta: 'COMBAT ABILITY',
            actions,
          },
        }))
      },

      flee() {
        if (this.state.busy) return
        playEffect('ui_click')
        gameStore.handleAction('Flee from combat', undefined, {
          source: 'player',
          category: 'move',
          tool: buildToolRef('free-text', 'flee', 'Flee'),
          tags: ['combat_modal', 'combat_flee'],
        })
      },

      startRoll() {
        playEffect('ui_click')
        this.update({ isRolling: true, wizardPhase: 'rolling' })
        setTimeout(() => {
          const roll = Math.floor(Math.random() * 20) + 1
          this.update({ isRolling: false, wizardPhase: 'result', lastRoll: roll })
        }, 1200)
      },

      finalizeWizard() {
        const roll = this.state.lastRoll
        playEffect('ui_close')
        this.update({ wizardOpen: false, wizardPhase: 'ready' })
        dispatcher.trigger('combat-modal', TOPICS.ui.diceRolled, { roll })
      },

      sendCustom() {
        const input = this.$('input')?.value?.trim()
        if (!input || this.state.busy) return
        playEffect('ui_click')
        gameStore.handleAction(input)
        this.$('input').value = ''
      },

      async useCombatItem(item) {
        if (this.state.busy) return
        const name = String(item?.name ?? '').trim()
        if (!name) return
        playEffect('ui_click')
        const gs = gameStore.state
        const preferred = await resolvePreferredCombatItemAction(name, item, {
          location: 'inventory',
          equipped: !!item?.equipped,
          gs,
        })
        if (preferred) {
          gameStore.handleAction(preferred.input, undefined, {
            source: 'item',
            mode: preferred.mode,
            category: preferred.category,
            tool: buildToolRef('item', `${name}_${preferred.id}`, `${name} · ${preferred.label}`),
            explicitTargets: [
              buildActionTarget('item', name),
              ...(preferred.explicitTargets ?? []),
            ],
            tags: ['combat_modal', ...(preferred.tags ?? []), `item_action_${preferred.id}`],
          })
          return
        }

        const target = String(this.state.activeTargetName ?? '').trim()
        const intent = target ? `Use ${name} on ${target}` : `Use ${name}`
        const explicitTargets = [buildActionTarget('item', name)]
        if (target) explicitTargets.push(buildActionTarget('npc', target))
        gameStore.handleAction(intent, undefined, {
          source: 'item',
          category: 'use',
          tool: buildToolRef('item', name, name),
          explicitTargets,
          tags: ['combat_modal', 'combat_item_use', 'item_use_improvise'],
        })
      },

      handleTargetAction(e) {
        const btn = e.target.closest('button[data-action="select-target"]')
        if (!btn) return
        const name = btn.getAttribute('data-target')
        if (!name || this.state.busy) return
        playEffect('ui_click')
        gameStore.selectCombatTarget(name)
      },

      handleParticipantAction(e) {
        const btn = e.target.closest('button[data-action="inspect-participant"]')
        if (!btn) return
        const participantId = btn.getAttribute('data-participant-id')
        const participant = this.state.participants?.find(p => p.id === participantId)
        if (!participant) return
        this.inspectParticipant(participant)
      },

      handleActionClick(e) {
        const btn = e.target.closest('button[data-action]')
        if (!btn) return
        const action = btn.getAttribute('data-action')
        if (this.state.busy && action !== 'close') return

        switch (action) {
          case 'attack':
            this.attackNpc()
            break
          case 'open-ability':
            const abilityName = btn.getAttribute('data-ability-name')
            const ability = this.state.readyAbilities?.find(a => a.name === abilityName)
            if (ability) this.openAbilityModal(ability)
            break
          case 'flee':
            this.flee()
            break
          case 'use-item':
            const itemName = btn.getAttribute('data-item-name')
            const item = this.state.combatItems?.find(i => i.name === itemName)
            if (item) this.useCombatItem(item)
            break
          case 'send-custom':
            this.sendCustom()
            break
          case 'close':
            this.requestClose()
            break
        }
      },

      handleWizardAction(e) {
        const btn = e.target.closest('button[data-action]')
        if (!btn) return
        const action = btn.getAttribute('data-action')
        if (action === 'start-roll') this.startRoll()
        if (action === 'finalize-wizard') this.finalizeWizard()
      },

      handleOutcomeAction(e) {
        const btn = e.target.closest('button[data-action]')
        if (!btn) return
        this.closeModal()
      },

      handleCustomInputKey(e) {
        if (e.key === 'Enter') this.sendCustom()
      }
    }
  </script>
</combat-modal>
